// AvailabilityManager.js - Version améliorée avec choix demi-journée/journée complète

import React, { useState, useEffect, useContext } from 'react';
import { firestore } from '../../firebase/config';
import { AuthContext } from '../../context/AuthContext';
import './AvailabilityManager.css';

const AvailabilityManager = () => {
  const { userProfile } = useContext(AuthContext);
  const [viewMode, setViewMode] = useState('weekly');
  const [editMode, setEditMode] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [loading, setLoading] = useState(false);

  // États pour les données
  const [weeklyAvailabilities, setWeeklyAvailabilities] = useState({});
  const [specificAvailabilities, setSpecificAvailabilities] = useState([]);

  // Ajouter ces états après les états existants (ligne ~15)
  const [copyMode, setCopyMode] = useState('none'); // 'none', 'from-date', 'from-template'
  const [sourceConfig, setSourceConfig] = useState(null); // Configuration à copier


  // État pour la navigation du calendrier
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Nouveau state pour le mode de saisie (demi-journée ou journée complète)
  const [inputMode, setInputMode] = useState('half-day'); // 'half-day' ou 'full-day'

  // État du formulaire avec les nouveaux champs pour le mode de saisie
  const [formData, setFormData] = useState({
    // Mode de saisie
    inputMode: 'half-day', // 'half-day' ou 'full-day'

    // Champs pour le mode journée complète
    fullDayAvailable: true,
    fullDayStartTime: '08:00',
    fullDayEndTime: '18:00',
    fullDayLunchBreakStart: '12:00',
    fullDayLunchBreakEnd: '13:30',
    fullDayHasLunchBreak: true,
    fullDayAllowsCabinet: true, // NOUVEAU : Cabinet activé par défaut
    fullDayAllowsRemote: false,
    fullDayAllowsHomeVisits: false,

    // Champs pour le mode demi-journée - Matin
    morningAvailable: true,
    morningStartTime: '08:00',
    morningEndTime: '12:00',
    morningLunchBreakStart: '',
    morningLunchBreakEnd: '',
    morningHasLunchBreak: false,
    morningAllowsCabinet: true, // NOUVEAU : Cabinet activé par défaut
    morningAllowsRemote: false,
    morningAllowsHomeVisits: false,

    // Champs pour le mode demi-journée - Après-midi
    afternoonAvailable: true,
    afternoonStartTime: '13:30',
    afternoonEndTime: '18:00',
    afternoonLunchBreakStart: '',
    afternoonLunchBreakEnd: '',
    afternoonHasLunchBreak: false,
    afternoonAllowsCabinet: true, // NOUVEAU : Cabinet activé par défaut
    afternoonAllowsRemote: false,
    afternoonAllowsHomeVisits: false,

    // Champs de compatibilité
    isWorkingDay: true,
    homeVisitRadius: 15,
    homeVisitTransportFee: 10,
  });

  // Jours de la semaine
  const weekDays = [
    { id: 0, name: 'Lundi' },
    { id: 1, name: 'Mardi' },
    { id: 2, name: 'Mercredi' },
    { id: 3, name: 'Jeudi' },
    { id: 4, name: 'Vendredi' },
    { id: 5, name: 'Samedi' },
    { id: 6, name: 'Dimanche' }
  ];

  // Mois en français
  const months = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const validateConsultationOptions = (config, period) => {
    if (period === 'fullDay') {
      return config.fullDayAllowsCabinet || config.fullDayAllowsRemote || config.fullDayAllowsHomeVisits;
    } else if (period === 'morning') {
      return config.morningAllowsCabinet || config.morningAllowsRemote || config.morningAllowsHomeVisits;
    } else if (period === 'afternoon') {
      return config.afternoonAllowsCabinet || config.afternoonAllowsRemote || config.afternoonAllowsHomeVisits;
    }
    return true;
  };

  // Charger les disponibilités
  useEffect(() => {
    const fetchAvailabilities = async () => {
      if (!userProfile?.practitionerId) return;

      setLoading(true);

      try {
        // Récupérer les disponibilités par défaut (hebdomadaires)
        const defaultSnapshot = await firestore
          .collection('availability')
          .where('practitionerId', '==', userProfile.practitionerId)
          .where('type', '==', 'default')
          .get();

        const weeklyData = {};
        defaultSnapshot.docs.forEach(doc => {
          const data = doc.data();
          weeklyData[data.dayOfWeek] = { id: doc.id, ...data };
        });

        setWeeklyAvailabilities(weeklyData);

        // Récupérer les disponibilités spécifiques
        const specificSnapshot = await firestore
          .collection('availability')
          .where('practitionerId', '==', userProfile.practitionerId)
          .where('type', '==', 'specific')
          .get();

        const specificData = specificSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setSpecificAvailabilities(specificData);

      } catch (error) {
        console.error('Erreur lors du chargement des disponibilités:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAvailabilities();
  }, [userProfile]);

  // Gérer le changement de mode de saisie
  const handleInputModeChange = (mode) => {
    setInputMode(mode);

    // Synchroniser les données selon le mode
    if (mode === 'full-day') {
      // Convertir les demi-journées vers journée complète
      setFormData(prevData => ({
        ...prevData,
        inputMode: mode,
        fullDayAvailable: prevData.morningAvailable || prevData.afternoonAvailable,
        fullDayStartTime: prevData.morningAvailable ? prevData.morningStartTime : prevData.afternoonStartTime,
        fullDayEndTime: prevData.afternoonAvailable ? prevData.afternoonEndTime : prevData.morningEndTime,
        fullDayHasLunchBreak: prevData.morningAvailable && prevData.afternoonAvailable,
        fullDayLunchBreakStart: prevData.morningAvailable && prevData.afternoonAvailable ? prevData.morningEndTime : '',
        fullDayLunchBreakEnd: prevData.morningAvailable && prevData.afternoonAvailable ? prevData.afternoonStartTime : '',
        isWorkingDay: prevData.morningAvailable || prevData.afternoonAvailable
      }));
    } else {
      // Convertir journée complète vers demi-journées
      setFormData(prevData => {
        if (prevData.fullDayHasLunchBreak && prevData.fullDayLunchBreakStart && prevData.fullDayLunchBreakEnd) {
          return {
            ...prevData,
            inputMode: mode,
            morningAvailable: prevData.fullDayAvailable,
            morningStartTime: prevData.fullDayStartTime,
            morningEndTime: prevData.fullDayLunchBreakStart,
            afternoonAvailable: prevData.fullDayAvailable,
            afternoonStartTime: prevData.fullDayLunchBreakEnd,
            afternoonEndTime: prevData.fullDayEndTime,
            isWorkingDay: prevData.fullDayAvailable
          };
        } else {
          // Diviser la journée en deux parties égales
          const startHour = parseInt(prevData.fullDayStartTime.split(':')[0]);
          const endHour = parseInt(prevData.fullDayEndTime.split(':')[0]);
          const midHour = Math.floor((startHour + endHour) / 2);

          return {
            ...prevData,
            inputMode: mode,
            morningAvailable: prevData.fullDayAvailable,
            morningStartTime: prevData.fullDayStartTime,
            morningEndTime: `${midHour.toString().padStart(2, '0')}:00`,
            afternoonAvailable: prevData.fullDayAvailable,
            afternoonStartTime: `${(midHour + 1).toString().padStart(2, '0')}:00`,
            afternoonEndTime: prevData.fullDayEndTime,
            isWorkingDay: prevData.fullDayAvailable
          };
        }
      });
    }
  };

  // Éditer une disponibilité (jour de la semaine ou date spécifique)
  const handleEditAvailability = (day = null, dateStr = null) => {
    let availability = null;

    if (dateStr) {
      // Mode spécifique par date
      availability = specificAvailabilities.find(sa => sa.date === dateStr);
      if (!availability && day !== null) {
        availability = weeklyAvailabilities[day];
      }
      setSelectedDate(dateStr);
      setSelectedDay(day);
    } else if (day !== null) {
      // Mode hebdomadaire
      availability = weeklyAvailabilities[day];
      setSelectedDay(day);
      setSelectedDate(null);
    }

    if (availability) {
      // Détecter le mode de saisie basé sur les données existantes
      const hasHalfDayData = availability.morningAvailable !== undefined || availability.afternoonAvailable !== undefined;
      const hasFullDayData = availability.fullDayAvailable !== undefined;

      let detectedMode = 'half-day';
      if (!hasHalfDayData && hasFullDayData) {
        detectedMode = 'full-day';
      } else if (availability.inputMode) {
        detectedMode = availability.inputMode;
      }

      setInputMode(detectedMode);

      setFormData({
        inputMode: detectedMode,

        // Mode journée complète
        fullDayAvailable: availability.fullDayAvailable !== undefined ? availability.fullDayAvailable : availability.isWorkingDay,
        fullDayStartTime: availability.fullDayStartTime || availability.startTime || '08:00',
        fullDayEndTime: availability.fullDayEndTime || availability.endTime || '18:00',
        fullDayLunchBreakStart: availability.fullDayLunchBreakStart || availability.lunchBreak?.start || '12:00',
        fullDayLunchBreakEnd: availability.fullDayLunchBreakEnd || availability.lunchBreak?.end || '13:30',
        fullDayHasLunchBreak: availability.fullDayHasLunchBreak !== undefined ? availability.fullDayHasLunchBreak : true,
        fullDayAllowsCabinet: availability.fullDayAllowsCabinet !== undefined ? availability.fullDayAllowsCabinet : true,
        fullDayAllowsRemote: availability.fullDayAllowsRemote !== undefined ? availability.fullDayAllowsRemote : false,
        fullDayAllowsHomeVisits: availability.fullDayAllowsHomeVisits !== undefined ? availability.fullDayAllowsHomeVisits : false,

        // Mode demi-journée - Matin
        morningAvailable: availability.morningAvailable !== undefined ? availability.morningAvailable : true,
        morningStartTime: availability.morningStartTime || '08:00',
        morningEndTime: availability.morningEndTime || '12:00',
        morningLunchBreakStart: availability.morningLunchBreakStart || '',
        morningLunchBreakEnd: availability.morningLunchBreakEnd || '',
        morningHasLunchBreak: availability.morningHasLunchBreak || false,
        morningAllowsCabinet: availability.morningAllowsCabinet !== undefined ? availability.morningAllowsCabinet : true,
        morningAllowsRemote: availability.morningAllowsRemote !== undefined ? availability.morningAllowsRemote : false,
        morningAllowsHomeVisits: availability.morningAllowsHomeVisits || false,

        // Mode demi-journée - Après-midi
        afternoonAvailable: availability.afternoonAvailable !== undefined ? availability.afternoonAvailable : true,
        afternoonStartTime: availability.afternoonStartTime || '13:30',
        afternoonEndTime: availability.afternoonEndTime || '18:00',
        afternoonLunchBreakStart: availability.afternoonLunchBreakStart || '',
        afternoonLunchBreakEnd: availability.afternoonLunchBreakEnd || '',
        afternoonHasLunchBreak: availability.afternoonHasLunchBreak || false,
        afternoonAllowsCabinet: availability.afternoonAllowsCabinet !== undefined ? availability.afternoonAllowsCabinet : true,
        afternoonAllowsRemote: availability.afternoonAllowsRemote !== undefined ? availability.afternoonAllowsRemote : false,
        afternoonAllowsHomeVisits: availability.afternoonAllowsHomeVisits || false,

        // Champs de compatibilité
        isWorkingDay: availability.isWorkingDay !== undefined ? availability.isWorkingDay : true,
        homeVisitRadius: availability.homeVisitRadius || 15,
        homeVisitTransportFee: availability.homeVisitTransportFee || 10,
      });
    }

    setEditMode(true);
  };

  // Mettre à jour les champs du formulaire
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let newFormData = {
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    };

    setFormData(newFormData);
  };

  // Sauvegarder les modifications
  const handleSaveAvailability = async (e) => {
    e.preventDefault();

    if (formData.inputMode === 'full-day') {
      if (!validateConsultationOptions(formData, 'fullDay')) {
        alert('Au moins une option de consultation doit être activée.');
        return;
      }
    } else {
      if (formData.morningAvailable && !validateConsultationOptions(formData, 'morning')) {
        alert('Au moins une option de consultation doit être activée pour le matin.');
        return;
      }
      if (formData.afternoonAvailable && !validateConsultationOptions(formData, 'afternoon')) {
        alert('Au moins une option de consultation doit être activée pour l\'après-midi.');
        return;
      }
    }

    if (!userProfile?.practitionerId) return;

    try {
      const baseData = {
        practitionerId: userProfile.practitionerId,
        inputMode: formData.inputMode,
        homeVisitRadius: formData.homeVisitRadius,
        homeVisitTransportFee: formData.homeVisitTransportFee,
        updatedAt: new Date()
      };

      let availabilityData;

      if (formData.inputMode === 'full-day') {
        // Mode journée complète
        availabilityData = {
          ...baseData,
          fullDayAvailable: formData.fullDayAvailable,
          fullDayStartTime: formData.fullDayStartTime,
          fullDayEndTime: formData.fullDayEndTime,
          fullDayLunchBreakStart: formData.fullDayLunchBreakStart,
          fullDayLunchBreakEnd: formData.fullDayLunchBreakEnd,
          fullDayHasLunchBreak: formData.fullDayHasLunchBreak,
          fullDayAllowsCabinet: formData.fullDayAllowsCabinet,
          fullDayAllowsRemote: formData.fullDayAllowsRemote,
          fullDayAllowsHomeVisits: formData.fullDayAllowsHomeVisits,

          // Champs de compatibilité
          isWorkingDay: formData.fullDayAvailable,
          startTime: formData.fullDayStartTime,
          endTime: formData.fullDayEndTime,
          lunchBreak: formData.fullDayHasLunchBreak ? {
            start: formData.fullDayLunchBreakStart,
            end: formData.fullDayLunchBreakEnd
          } : null
        };
      } else {
        // Mode demi-journée
        availabilityData = {
          ...baseData,
          morningAvailable: formData.morningAvailable,
          morningStartTime: formData.morningStartTime,
          morningEndTime: formData.morningEndTime,
          morningLunchBreakStart: formData.morningLunchBreakStart,
          morningLunchBreakEnd: formData.morningLunchBreakEnd,
          morningHasLunchBreak: formData.morningHasLunchBreak,
          morningAllowsCabinet: formData.morningAllowsCabinet,
          morningAllowsRemote: formData.morningAllowsRemote,
          morningAllowsHomeVisits: formData.morningAllowsHomeVisits,

          afternoonAvailable: formData.afternoonAvailable,
          afternoonStartTime: formData.afternoonStartTime,
          afternoonEndTime: formData.afternoonEndTime,
          afternoonLunchBreakStart: formData.afternoonLunchBreakStart,
          afternoonLunchBreakEnd: formData.afternoonLunchBreakEnd,
          afternoonHasLunchBreak: formData.afternoonHasLunchBreak,
          afternoonAllowsCabinet: formData.afternoonAllowsCabinet,
          afternoonAllowsRemote: formData.afternoonAllowsRemote,
          afternoonAllowsHomeVisits: formData.afternoonAllowsHomeVisits,

          // Champs de compatibilité
          isWorkingDay: formData.morningAvailable || formData.afternoonAvailable,
          startTime: formData.morningAvailable ? formData.morningStartTime : formData.afternoonStartTime,
          endTime: formData.afternoonAvailable ? formData.afternoonEndTime : formData.morningEndTime,
          lunchBreak: formData.morningAvailable && formData.afternoonAvailable ? {
            start: formData.morningEndTime,
            end: formData.afternoonStartTime
          } : null
        };
      }

      if (selectedDate) {
        // Sauvegarde pour une date spécifique
        availabilityData.type = 'specific';
        availabilityData.date = selectedDate;

        const existingSpecific = specificAvailabilities.find(sa => sa.date === selectedDate);

        if (existingSpecific) {
          await firestore
            .collection('availability')
            .doc(existingSpecific.id)
            .update(availabilityData);

          setSpecificAvailabilities(specificAvailabilities.map(sa =>
            sa.date === selectedDate
              ? { ...sa, ...availabilityData }
              : sa
          ));
        } else {
          const docRef = await firestore
            .collection('availability')
            .add(availabilityData);

          setSpecificAvailabilities([...specificAvailabilities, {
            id: docRef.id,
            ...availabilityData
          }]);
        }
      } else if (selectedDay !== null) {
        // Sauvegarde pour un jour de la semaine
        availabilityData.type = 'default';
        availabilityData.dayOfWeek = selectedDay;

        const existingWeekly = weeklyAvailabilities[selectedDay];

        if (existingWeekly) {
          await firestore
            .collection('availability')
            .doc(existingWeekly.id)
            .update(availabilityData);

          setWeeklyAvailabilities({
            ...weeklyAvailabilities,
            [selectedDay]: { ...existingWeekly, ...availabilityData }
          });
        } else {
          const docRef = await firestore
            .collection('availability')
            .add(availabilityData);

          setWeeklyAvailabilities({
            ...weeklyAvailabilities,
            [selectedDay]: { id: docRef.id, ...availabilityData }
          });
        }
      }

      setEditMode(false);
      setSelectedDay(null);
      setSelectedDate(null);

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  // Ajouter une fonction utilitaire pour gérer la compatibilité ascendante
  const getMigrationCompatibleValue = (availabilityData, field) => {
    // Si le nouveau champ existe, l'utiliser
    if (availabilityData[field] !== undefined) {
      return availabilityData[field];
    }

    // Sinon, utiliser des valeurs par défaut selon le champ
    if (field.includes('AllowsCabinet')) {
      return true; // Cabinet activé par défaut pour compatibilité
    }

    return false; // Remote et Home désactivés par défaut
  };

  // Fonction utilitaire pour afficher les informations selon le mode
  const formatAvailabilityInfo = (availability) => {
    if (!availability) return 'Non configuré';

    const inputMode = availability.inputMode || 'half-day';

    if (inputMode === 'full-day') {
      if (!availability.fullDayAvailable) return 'Fermé';

      let info = `${availability.fullDayStartTime || availability.startTime} - ${availability.fullDayEndTime || availability.endTime}`;

      if (availability.fullDayHasLunchBreak && availability.fullDayLunchBreakStart && availability.fullDayLunchBreakEnd) {
        info += ` (Pause: ${availability.fullDayLunchBreakStart}-${availability.fullDayLunchBreakEnd})`;
      }


      const options = [];
      if (availability.fullDayAllowsCabinet !== false) options.push('Cabinet');
      if (availability.fullDayAllowsRemote) options.push('Distance');
      if (availability.fullDayAllowsHomeVisits) options.push('Domicile');
      info += ` (${options.join(' & ')})`;

      return info;
    } else {


      // Mode demi-journée (format existant)
      const formatHalfDay = (availability, period) => {
        debugger
        const prefix = period;
        const isAvailable = availability[`${prefix}Available`];

        if (!isAvailable) return 'Fermé';

        const startTime = availability[`${prefix}StartTime`];
        const endTime = availability[`${prefix}EndTime`];

        // Utiliser les nouveaux champs avec fallback
        const allowsCabinet = availability[`${prefix}AllowsCabinet`] !== false;;
        const allowsRemote = availability[`${prefix}AllowsRemote`];
        const allowsHomeVisits = availability[`${prefix}AllowsHomeVisits`];

        let info = `${startTime} - ${endTime}`;

        const options = [];
        if (allowsCabinet) options.push('Cabinet');
        if (allowsRemote) options.push('Distance');
        if (allowsHomeVisits) options.push('Domicile');

        info += ` (${options.join(' & ')})`;

        return info;
      };

      const morningInfo = formatHalfDay(availability, 'morning');
      const afternoonInfo = formatHalfDay(availability, 'afternoon');

      return (
        <div className="half-day-summary">
          <div className="half-day-info">
            <strong>Matin:</strong> {morningInfo}
          </div>
          <div className="half-day-info">
            <strong>Après-midi:</strong> {afternoonInfo}
          </div>
        </div>
      );
    }
  };

  // 1. Fonction pour extraire la configuration d'une date
  const extractConfigFromDate = (dateStr) => {
    const specificAvailability = specificAvailabilities.find(sa => sa.date === dateStr);
    const dayOfWeek = new Date(dateStr).getDay();
    const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const defaultAvailability = weeklyAvailabilities[adjustedDayOfWeek];

    const availability = specificAvailability || defaultAvailability;

    if (!availability) return null;

    return {
      inputMode: availability.inputMode || 'half-day',

      // Mode journée complète
      fullDayAvailable: availability.fullDayAvailable,
      fullDayStartTime: availability.fullDayStartTime,
      fullDayEndTime: availability.fullDayEndTime,
      fullDayLunchBreakStart: availability.fullDayLunchBreakStart,
      fullDayLunchBreakEnd: availability.fullDayLunchBreakEnd,
      fullDayHasLunchBreak: availability.fullDayHasLunchBreak,
      fullDayAllowsRemote: availability.fullDayAllowsRemote,
      fullDayAllowsHomeVisits: availability.fullDayAllowsHomeVisits,

      // Mode demi-journée
      morningAvailable: availability.morningAvailable,
      morningStartTime: availability.morningStartTime,
      morningEndTime: availability.morningEndTime,
      morningLunchBreakStart: availability.morningLunchBreakStart,
      morningLunchBreakEnd: availability.morningLunchBreakEnd,
      morningHasLunchBreak: availability.morningHasLunchBreak,
      morningAllowsRemote: availability.morningAllowsRemote,
      morningAllowsHomeVisits: availability.morningAllowsHomeVisits,

      afternoonAvailable: availability.afternoonAvailable,
      afternoonStartTime: availability.afternoonStartTime,
      afternoonEndTime: availability.afternoonEndTime,
      afternoonLunchBreakStart: availability.afternoonLunchBreakStart,
      afternoonLunchBreakEnd: availability.afternoonLunchBreakEnd,
      afternoonHasLunchBreak: availability.afternoonHasLunchBreak,
      afternoonAllowsRemote: availability.afternoonAllowsRemote,
      afternoonAllowsHomeVisits: availability.afternoonAllowsHomeVisits,

      // Paramètres communs
      homeVisitRadius: availability.homeVisitRadius || 15,
      homeVisitTransportFee: availability.homeVisitTransportFee || 10,
    };
  };

  // 2. Fonction pour définir une date comme source de copie
  const setAsSourceForCopy = (dateStr) => {
    const config = extractConfigFromDate(dateStr);
    if (config) {
      setSourceConfig({
        ...config,
        sourceDate: dateStr,
        sourceName: `Configuration du ${new Date(dateStr).toLocaleDateString('fr-FR')}`
      });
      setCopyMode('from-date');

      // Ajouter cette date aux sélectionnées si pas déjà fait
      if (!selectedDates.includes(dateStr)) {
        setSelectedDates([...selectedDates, dateStr]);
      }
    }
  };

  // 4. Fonction pour appliquer la configuration source aux dates sélectionnées
  const applyCopyConfiguration = async () => {
    if (!sourceConfig || selectedDates.length === 0) return;

    setIsDuplicating(true);

    try {
      const configToSave = {
        practitionerId: userProfile.practitionerId,
        type: 'specific',
        inputMode: sourceConfig.inputMode,
        homeVisitRadius: sourceConfig.homeVisitRadius,
        homeVisitTransportFee: sourceConfig.homeVisitTransportFee,
        updatedAt: new Date()
      };

      // Ajouter les champs selon le mode
      if (sourceConfig.inputMode === 'full-day') {
        Object.assign(configToSave, {
          fullDayAvailable: sourceConfig.fullDayAvailable,
          fullDayStartTime: sourceConfig.fullDayStartTime,
          fullDayEndTime: sourceConfig.fullDayEndTime,
          fullDayLunchBreakStart: sourceConfig.fullDayLunchBreakStart,
          fullDayLunchBreakEnd: sourceConfig.fullDayLunchBreakEnd,
          fullDayHasLunchBreak: sourceConfig.fullDayHasLunchBreak,
          fullDayAllowsCabinet: sourceConfig.fullDayAllowsCabinet,
          fullDayAllowsRemote: sourceConfig.fullDayAllowsRemote,
          fullDayAllowsHomeVisits: sourceConfig.fullDayAllowsHomeVisits,

          // Champs de compatibilité
          isWorkingDay: sourceConfig.fullDayAvailable,
          startTime: sourceConfig.fullDayStartTime,
          endTime: sourceConfig.fullDayEndTime,
          lunchBreak: sourceConfig.fullDayHasLunchBreak ? {
            start: sourceConfig.fullDayLunchBreakStart,
            end: sourceConfig.fullDayLunchBreakEnd
          } : null
        });
      } else {
        Object.assign(configToSave, {
          morningAvailable: sourceConfig.morningAvailable,
          morningStartTime: sourceConfig.morningStartTime,
          morningEndTime: sourceConfig.morningEndTime,
          morningLunchBreakStart: sourceConfig.morningLunchBreakStart,
          morningLunchBreakEnd: sourceConfig.morningLunchBreakEnd,
          morningHasLunchBreak: sourceConfig.morningHasLunchBreak,
          morningAllowsCabinet: sourceConfig.morningAllowsCabinet,
          morningAllowsRemote: sourceConfig.morningAllowsRemote,
          morningAllowsHomeVisits: sourceConfig.morningAllowsHomeVisits,

          afternoonAvailable: sourceConfig.afternoonAvailable,
          afternoonStartTime: sourceConfig.afternoonStartTime,
          afternoonEndTime: sourceConfig.afternoonEndTime,
          afternoonLunchBreakStart: sourceConfig.afternoonLunchBreakStart,
          afternoonLunchBreakEnd: sourceConfig.afternoonLunchBreakEnd,
          afternoonHasLunchBreak: sourceConfig.afternoonHasLunchBreak,
          afternoonAllowsCabinet: sourceConfig.afternoonAllowsCabinet,
          afternoonAllowsRemote: sourceConfig.afternoonAllowsRemote,
          afternoonAllowsHomeVisits: sourceConfig.afternoonAllowsHomeVisits,

          // Champs de compatibilité
          isWorkingDay: sourceConfig.morningAvailable || sourceConfig.afternoonAvailable,
          startTime: sourceConfig.morningAvailable ? sourceConfig.morningStartTime : sourceConfig.afternoonStartTime,
          endTime: sourceConfig.afternoonAvailable ? sourceConfig.afternoonEndTime : sourceConfig.morningEndTime,
          lunchBreak: sourceConfig.morningAvailable && sourceConfig.afternoonAvailable ? {
            start: sourceConfig.morningEndTime,
            end: sourceConfig.afternoonStartTime
          } : null
        });
      }

      // Sauvegarder pour chaque date sélectionnée
      const promises = selectedDates.map(async (dateStr) => {
        const dataToSave = { ...configToSave, date: dateStr };

        const existing = specificAvailabilities.find(sa => sa.date === dateStr);

        if (existing) {
          return firestore
            .collection('availability')
            .doc(existing.id)
            .update(dataToSave);
        } else {
          return firestore
            .collection('availability')
            .add(dataToSave);
        }
      });

      await Promise.all(promises);

      // Mettre à jour l'état local
      const updatedSpecifics = [...specificAvailabilities];
      selectedDates.forEach(dateStr => {
        const existingIndex = updatedSpecifics.findIndex(sa => sa.date === dateStr);
        if (existingIndex >= 0) {
          updatedSpecifics[existingIndex] = {
            ...updatedSpecifics[existingIndex],
            ...configToSave,
            date: dateStr
          };
        } else {
          updatedSpecifics.push({
            id: `temp-${dateStr}`,
            ...configToSave,
            date: dateStr
          });
        }
      });

      setSpecificAvailabilities(updatedSpecifics);

      // Nettoyer après application
      setSelectedDates([]);
      setMultiSelectMode(false);
      setCopyMode('none');
      setSourceConfig(null);

      alert(`Configuration "${sourceConfig.sourceName}" appliquée à ${selectedDates.length} date(s) avec succès !`);

    } catch (error) {
      console.error('Erreur lors de l\'application de la configuration:', error);
      alert('Erreur lors de l\'application de la configuration');
    } finally {
      setIsDuplicating(false);
    }
  };

  // 5. Fonction pour annuler le mode copie
  const cancelCopyMode = () => {
    setCopyMode('none');
    setSourceConfig(null);
    setSelectedDates([]);
    setMultiSelectMode(false);
  };

  // Supprimer une disponibilité spécifique
  const handleDeleteSpecificAvailability = async (dateStr) => {
    // Utiliser window.confirm explicitement pour éviter l'erreur ESLint
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette disponibilité spécifique ? Les réglages par défaut du jour de la semaine seront utilisés.')) {
      return;
    }

    try {
      const specificToDelete = specificAvailabilities.find(sa => sa.date === dateStr);

      if (specificToDelete && specificToDelete.id) {
        await firestore
          .collection('availability')
          .doc(specificToDelete.id)
          .delete();

        // Mettre à jour l'état local
        setSpecificAvailabilities(specificAvailabilities.filter(sa => sa.date !== dateStr));

        // Si c'était la date sélectionnée, annuler l'édition
        if (selectedDate === dateStr) {
          setEditMode(false);
          setSelectedDate(null);
        }
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedDay(null);
    setSelectedDate(null);
  };

  // Navigation mois précédent/suivant
  const handleMonthChange = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  // Render du formulaire avec le nouveau sélecteur de mode
  const renderForm = () => {
    if (!editMode) return null;

    const title = selectedDate
      ? `Disponibilité du ${selectedDate}`
      : selectedDay !== null
        ? `Disponibilité du ${weekDays[selectedDay].name}`
        : 'Nouvelle disponibilité';

    return (
      <div className="availability-form">
        <h3>{title}</h3>

        {/* Sélecteur de mode de saisie */}
        <div className="input-mode-selector">
          <h4>Mode de saisie</h4>
          <div className="mode-toggle">
            <label className="mode-option">
              <input
                type="radio"
                name="inputMode"
                value="half-day"
                checked={formData.inputMode === 'half-day'}
                onChange={(e) => handleInputModeChange(e.target.value)}
              />
              <span>Demi-journées</span>
              <small>Gérer séparément matin et après-midi</small>
            </label>

            <label className="mode-option">
              <input
                type="radio"
                name="inputMode"
                value="full-day"
                checked={formData.inputMode === 'full-day'}
                onChange={(e) => handleInputModeChange(e.target.value)}
              />
              <span>Journée complète</span>
              <small>Gérer comme une seule période</small>
            </label>
          </div>
        </div>

        <form onSubmit={handleSaveAvailability}>
          {formData.inputMode === 'full-day' ? (
            // Formulaire journée complète
            <div className="full-day-section">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="fullDayAvailable"
                    checked={formData.fullDayAvailable}
                    onChange={handleInputChange}
                  />
                  Disponible cette journée
                </label>
              </div>

              {formData.fullDayAvailable && (
                <>
                  <div className="time-inputs">
                    <div className="form-group">
                      <label>
                        Heure de début:
                        <input
                          type="time"
                          name="fullDayStartTime"
                          value={formData.fullDayStartTime}
                          onChange={handleInputChange}
                          required
                        />
                      </label>
                    </div>

                    <div className="form-group">
                      <label>
                        Heure de fin:
                        <input
                          type="time"
                          name="fullDayEndTime"
                          value={formData.fullDayEndTime}
                          onChange={handleInputChange}
                          required
                        />
                      </label>
                    </div>
                  </div>

                  <div className="form-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="fullDayHasLunchBreak"
                        checked={formData.fullDayHasLunchBreak}
                        onChange={handleInputChange}
                      />
                      Pause déjeuner
                    </label>
                  </div>

                  {formData.fullDayHasLunchBreak && (
                    <div className="lunch-break-inputs">
                      <div className="form-group">
                        <label>
                          Début pause:
                          <input
                            type="time"
                            name="fullDayLunchBreakStart"
                            value={formData.fullDayLunchBreakStart}
                            onChange={handleInputChange}
                          />
                        </label>
                      </div>

                      <div className="form-group">
                        <label>
                          Fin pause:
                          <input
                            type="time"
                            name="fullDayLunchBreakEnd"
                            value={formData.fullDayLunchBreakEnd}
                            onChange={handleInputChange}
                          />
                        </label>
                      </div>
                    </div>
                  )}

                  <div className="service-options-section">
                    <h4>Types de consultation</h4>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="fullDayAllowsCabinet"
                          checked={formData.fullDayAllowsCabinet}
                          onChange={handleInputChange}
                        />
                        Consultations en cabinet
                      </label>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="fullDayAllowsRemote"
                          checked={formData.fullDayAllowsRemote}
                          onChange={handleInputChange}
                        />
                        Consultations à distance
                      </label>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="fullDayAllowsHomeVisits"
                          checked={formData.fullDayAllowsHomeVisits}
                          onChange={handleInputChange}
                        />
                        Consultations à domicile
                      </label>
                    </div>



                  </div>
                </>
              )}
            </div>
          ) : (
            // Formulaire demi-journées (existant)
            <>
              {/* Section Matin */}
              <div className="half-day-section">
                <h4 className="half-day-title">🌅 Matinée</h4>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="morningAvailable"
                      checked={formData.morningAvailable}
                      onChange={handleInputChange}
                    />
                    Disponible le matin
                  </label>
                </div>

                {formData.morningAvailable && (
                  <>
                    <div className="time-inputs">
                      <div className="form-group">
                        <label>
                          Heure de début:
                          <input
                            type="time"
                            name="morningStartTime"
                            value={formData.morningStartTime}
                            onChange={handleInputChange}
                            required
                          />
                        </label>
                      </div>

                      <div className="form-group">
                        <label>
                          Heure de fin:
                          <input
                            type="time"
                            name="morningEndTime"
                            value={formData.morningEndTime}
                            onChange={handleInputChange}
                            required
                          />
                        </label>
                      </div>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="morningHasLunchBreak"
                          checked={formData.morningHasLunchBreak}
                          onChange={handleInputChange}
                        />
                        Pause pendant la matinée
                      </label>
                    </div>

                    {formData.morningHasLunchBreak && (
                      <div className="lunch-break-inputs">
                        <div className="form-group">
                          <label>
                            Début pause:
                            <input
                              type="time"
                              name="morningLunchBreakStart"
                              value={formData.morningLunchBreakStart}
                              onChange={handleInputChange}
                            />
                          </label>
                        </div>

                        <div className="form-group">
                          <label>
                            Fin pause:
                            <input
                              type="time"
                              name="morningLunchBreakEnd"
                              value={formData.morningLunchBreakEnd}
                              onChange={handleInputChange}
                            />
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="service-options-section">
                      <h5>Types de consultation (matin)</h5>

                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            name="morningAllowsCabinet"
                            checked={formData.morningAllowsCabinet}
                            onChange={handleInputChange}
                          />
                          Consultations en cabinet
                        </label>
                      </div>

                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            name="morningAllowsRemote"
                            checked={formData.morningAllowsRemote}
                            onChange={handleInputChange}
                          />
                          Consultations à distance
                        </label>
                      </div>

                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            name="morningAllowsHomeVisits"
                            checked={formData.morningAllowsHomeVisits}
                            onChange={handleInputChange}
                          />
                          Consultations à domicile
                        </label>
                      </div>

                    </div>
                  </>
                )}
              </div>

              {/* Section Après-midi */}
              <div className="half-day-section">
                <h4 className="half-day-title">🌇 Après-midi</h4>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="afternoonAvailable"
                      checked={formData.afternoonAvailable}
                      onChange={handleInputChange}
                    />
                    Disponible l'après-midi
                  </label>
                </div>

                {formData.afternoonAvailable && (
                  <>
                    <div className="time-inputs">
                      <div className="form-group">
                        <label>
                          Heure de début:
                          <input
                            type="time"
                            name="afternoonStartTime"
                            value={formData.afternoonStartTime}
                            onChange={handleInputChange}
                            required
                          />
                        </label>
                      </div>

                      <div className="form-group">
                        <label>
                          Heure de fin:
                          <input
                            type="time"
                            name="afternoonEndTime"
                            value={formData.afternoonEndTime}
                            onChange={handleInputChange}
                            required
                          />
                        </label>
                      </div>
                    </div>

                    <div className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          name="afternoonHasLunchBreak"
                          checked={formData.afternoonHasLunchBreak}
                          onChange={handleInputChange}
                        />
                        Pause pendant l'après-midi
                      </label>
                    </div>

                    {formData.afternoonHasLunchBreak && (
                      <div className="lunch-break-inputs">
                        <div className="form-group">
                          <label>
                            Début pause:
                            <input
                              type="time"
                              name="afternoonLunchBreakStart"
                              value={formData.afternoonLunchBreakStart}
                              onChange={handleInputChange}
                            />
                          </label>
                        </div>

                        <div className="form-group">
                          <label>
                            Fin pause:
                            <input
                              type="time"
                              name="afternoonLunchBreakEnd"
                              value={formData.afternoonLunchBreakEnd}
                              onChange={handleInputChange}
                            />
                          </label>
                        </div>
                      </div>
                    )}

                    <div className="service-options-section">
                      <h5>Types de consultation (après-midi)</h5>

                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            name="afternoonAllowsCabinet"
                            checked={formData.afternoonAllowsCabinet}
                            onChange={handleInputChange}
                          />
                          Consultations en cabinet
                        </label>
                      </div>

                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            name="afternoonAllowsRemote"
                            checked={formData.afternoonAllowsRemote}
                            onChange={handleInputChange}
                          />
                          Consultations à distance
                        </label>
                      </div>

                      <div className="form-group checkbox-group">
                        <label>
                          <input
                            type="checkbox"
                            name="afternoonAllowsHomeVisits"
                            checked={formData.afternoonAllowsHomeVisits}
                            onChange={handleInputChange}
                          />
                          Consultations à domicile
                        </label>
                      </div>


                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* Section commune pour les visites à domicile */}
          {((formData.inputMode === 'full-day' && formData.fullDayAllowsHomeVisits) ||
            (formData.inputMode === 'half-day' && (formData.morningAllowsHomeVisits || formData.afternoonAllowsHomeVisits))) && (
              <div className="home-visit-section">
                <h4>Configuration des visites à domicile</h4>

                <div className="form-group">
                  <label>
                    Rayon d'intervention (km):
                    <input
                      type="number"
                      name="homeVisitRadius"
                      value={formData.homeVisitRadius}
                      onChange={handleInputChange}
                      min="1"
                      max="100"
                    />
                  </label>
                </div>

                <div className="form-group">
                  <label>
                    Frais de déplacement (€):
                    <input
                      type="number"
                      name="homeVisitTransportFee"
                      value={formData.homeVisitTransportFee}
                      onChange={handleInputChange}
                      min="0"
                      step="0.50"
                    />
                  </label>
                </div>
              </div>
            )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              Sauvegarder
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancelEdit}
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Render des jours de la semaine avec affichage adapté au mode
  const renderWeeklyView = () => (
    <div className="availability-list">
      <h3>Disponibilités par défaut</h3>
      <p>Configurez vos horaires standards pour chaque jour de la semaine.</p>

      <div className="week-days-list">
        {weekDays.map(day => {
          const availability = weeklyAvailabilities[day.id];
          const isSelected = selectedDay === day.id && !selectedDate;

          return (
            <div
              key={day.id}
              className={`day-card ${isSelected ? 'selected' : ''} ${!availability?.isWorkingDay ? 'day-off' : ''
                }`}
              onClick={() => handleEditAvailability(day.id)}
            >
              <div className="day-name">{day.name}</div>
              <div className="day-hours">
                {formatAvailabilityInfo(availability)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Générer le calendrier pour le mois sélectionné (version originale)
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1);
    const lastDayOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
    const startDate = new Date(firstDayOfMonth);
    const endDate = new Date(lastDayOfMonth);

    // Ajuster pour commencer le lundi
    const startDay = startDate.getDay();
    const daysToSubtract = startDay === 0 ? 6 : startDay - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    // Ajuster pour finir le dimanche
    const endDay = endDate.getDay();
    const daysToAdd = endDay === 0 ? 0 : 7 - endDay;
    endDate.setDate(endDate.getDate() + daysToAdd);

    const days = [];
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      const dateStr = currentDate.toLocaleDateString('fr-CA');
      const dayOfWeek = currentDate.getDay();
      const adjustedDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1;

      // Vérifier s'il y a une disponibilité spécifique pour cette date
      const specificAvailability = specificAvailabilities.find(sa => sa.date === dateStr);

      // Sinon, utiliser la disponibilité par défaut du jour de la semaine
      const defaultAvailability = weeklyAvailabilities[adjustedDayOfWeek];

      days.push({
        date: dateStr,
        dayNumber: currentDate.getDate(),
        isCurrentMonth: currentDate.getMonth() === selectedMonth,
        dayOfWeek: adjustedDayOfWeek,
        specificAvailability,
        defaultAvailability,
        hasSpecificSettings: !!specificAvailability,
        isSelected: selectedDate === dateStr || selectedDates.includes(dateStr)
      });

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  // Gérer le clic sur une date (version originale)
  // Gérer le clic sur une date
  const handleDateClick = (day) => {
    if (multiSelectMode) {
      // Mode sélection multiple
      if (selectedDates.includes(day.date)) {
        setSelectedDates(selectedDates.filter(d => d !== day.date));
      } else {
        setSelectedDates([...selectedDates, day.date]);
      }
    } else {
      // Mode sélection simple pour édition
      if (selectedDate === day.date) {
        // Désélectionner si déjà sélectionné
        setEditMode(false);
        setSelectedDate(null);
        setSelectedDay(null);
      } else {
        // Sélectionner cette date pour édition
        handleEditAvailability(day.dayOfWeek, day.date);
      }
    }
  };

  // Render du calendrier avec style original
  const renderSpecificView = () => {
    const calendarDays = generateCalendarDays();
    const weekDaysShort = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    return (
      <div className="specific-availability-section">
        {/* En-tête du calendrier */}
        <div className="calendar-mng-header">
          <button
            className="btn-icon"
            onClick={() => handleMonthChange('prev')}
            aria-label="Mois précédent"
          >
            ‹
          </button>
          <h3>{months[selectedMonth]} {selectedYear}</h3>
          <button
            className="btn-icon"
            onClick={() => handleMonthChange('next')}
            aria-label="Mois suivant"
          >
            ›
          </button>
        </div>

        {/* Contrôles de sélection multiple et copie */}
        <div className="multi-select-controls">
          <div className="control-section">
            <div className="multi-select-toggle">
              <label>
                <input
                  type="checkbox"
                  checked={multiSelectMode}
                  onChange={(e) => {
                    setMultiSelectMode(e.target.checked);
                    if (!e.target.checked) {
                      setSelectedDates([]);
                      setCopyMode('none');
                      setSourceConfig(null);
                    }
                  }}
                />
                Sélection multiple/Copie de configuration
              </label>
            </div>

            {multiSelectMode && (
              <div className="selection-summary">
                <span className="selected-count">
                  {selectedDates.length} date(s) sélectionnée(s)
                </span>
                {selectedDates.length > 0 && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setSelectedDates([]);
                      setCopyMode('none');
                      setSourceConfig(null);
                    }}
                  >
                    Tout désélectionner
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Section de copie de configuration */}
          {multiSelectMode && (
            <div className="copy-config-section">
              <h4>
                <i className="fas fa-copy"></i>
                Copier une configuration
              </h4>

              {/* Méthodes de copie */}
              <div className="copy-methods">
                <div className="copy-method">
                  <h5>📅 Copier depuis une date existante</h5>
                  <p>Sélectionnez une date qui a déjà une configuration pour la copier</p>

                  {copyMode === 'from-date' && sourceConfig ? (
                    <div className="source-selected">
                      <div className="source-info">
                        <i className="fas fa-check-circle"></i>
                        <strong>Source :</strong> {sourceConfig.sourceName}
                      </div>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => {
                          setCopyMode('none');
                          setSourceConfig(null);
                        }}
                      >
                        Changer
                      </button>
                    </div>
                  ) : (
                    <div className="instruction">
                      Cliquez sur le bouton "📋" à côté d'une date configurée dans le calendrier
                    </div>
                  )}
                </div>

                {/* <div className="copy-method">
                  <h5>📝 Utiliser un modèle prédéfini</h5>
                  <div className="template-buttons">
                    <button
                      className={`btn btn-template ${copyMode === 'from-template' && sourceConfig?.sourceName === 'Horaires bureau standard' ? 'active' : ''}`}
                      onClick={() => copyFromTemplate('bureau-standard')}
                    >
                      <i className="fas fa-building"></i>
                      Bureau standard
                      <small>9h-12h / 14h-18h</small>
                    </button>

                    <button
                      className={`btn btn-template ${copyMode === 'from-template' && sourceConfig?.sourceName === 'Journée continue' ? 'active' : ''}`}
                      onClick={() => copyFromTemplate('journee-continue')}
                    >
                      <i className="fas fa-clock"></i>
                      Journée continue
                      <small>8h-17h avec pause</small>
                    </button>

                    <button
                      className={`btn btn-template ${copyMode === 'from-template' && sourceConfig?.sourceName === 'Consultations à distance uniquement' ? 'active' : ''}`}
                      onClick={() => copyFromTemplate('distance-uniquement')}
                    >
                      <i className="fas fa-video"></i>
                      Distance uniquement
                      <small>Téléconsultations</small>
                    </button>

                    <button
                      className={`btn btn-template ${copyMode === 'from-template' && sourceConfig?.sourceName === 'Visites à domicile uniquement' ? 'active' : ''}`}
                      onClick={() => copyFromTemplate('domicile-uniquement')}
                    >
                      <i className="fas fa-home"></i>
                      Domicile uniquement
                      <small>Visites à domicile</small>
                    </button>

                    <button
                      className={`btn btn-template ${copyMode === 'from-template' && sourceConfig?.sourceName === 'Fermé' ? 'active' : ''}`}
                      onClick={() => copyFromTemplate('ferme')}
                    >
                      <i className="fas fa-times-circle"></i>
                      Fermé
                      <small>Pas de consultations</small>
                    </button>
                  </div>
                </div> */}
              </div>

              {/* Actions de copie */}
              {sourceConfig && selectedDates.length > 0 && (
                <div className="copy-actions">
                  <div className="copy-preview">
                    <h5>Aperçu de la configuration à appliquer :</h5>
                    <div className="config-preview">
                      <strong>{sourceConfig.sourceName}</strong>
                      {sourceConfig.inputMode === 'full-day' ? (
                        <div className="preview-details">
                          {sourceConfig.fullDayAvailable ? (
                            <>
                              <div>🕐 {sourceConfig.fullDayStartTime} - {sourceConfig.fullDayEndTime}</div>
                              {sourceConfig.fullDayHasLunchBreak && (
                                <div>🍽️ Pause : {sourceConfig.fullDayLunchBreakStart} - {sourceConfig.fullDayLunchBreakEnd}</div>
                              )}
                              <div className="service-types">
                                {
                                  '🏢 Cabinet' +
                                  (sourceConfig.fullDayAllowsRemote ? ' + 💻 Distance' : '') +
                                  (sourceConfig.fullDayAllowsHomeVisits ? ' + 🏠 Domicile' : '')
                                }
                              </div>
                            </>
                          ) : (
                            <div className="closed">❌ Fermé</div>
                          )}
                        </div>
                      ) : (
                        <div className="preview-details">
                          <div className="half-day">
                            <strong>Matin :</strong> {sourceConfig.morningAvailable ? `${sourceConfig.morningStartTime} - ${sourceConfig.morningEndTime}` : 'Fermé'}
                          </div>
                          <div className="half-day">
                            <strong>Après-midi :</strong> {sourceConfig.afternoonAvailable ? `${sourceConfig.afternoonStartTime} - ${sourceConfig.afternoonEndTime}` : 'Fermé'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="apply-actions">
                    <button
                      className="btn btn-primary"
                      onClick={applyCopyConfiguration}
                      disabled={isDuplicating}
                    >
                      {isDuplicating ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i>
                          Application en cours...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-paste"></i>
                          Appliquer à {selectedDates.length} date(s)
                        </>
                      )}
                    </button>

                    <button
                      className="btn btn-secondary"
                      onClick={cancelCopyMode}
                    >
                      <i className="fas fa-times"></i>
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Indicateur de mode actif */}
        {multiSelectMode && (
          <div className="selection-mode-indicator">
            <i className="fas fa-info-circle"></i>
            {copyMode === 'none'
              ? 'Mode sélection multiple activé - Cliquez sur les dates à configurer'
              : `Mode copie activé - Configuration source : ${sourceConfig?.sourceName}`
            }
          </div>
        )}

        {/* Grille du calendrier avec boutons de copie */}
        <div className={`calendar-mng-grid ${multiSelectMode ? 'selection-mode' : ''}`}>
          {/* En-têtes des jours de la semaine */}
          <div className="calendar-mng-weekdays">
            {weekDaysShort.map(day => (
              <div key={day} className="weekday">
                {day}
              </div>
            ))}
          </div>

          {/* Jours du calendrier */}
          <div className="calendar-mng-days">
            {calendarDays.map((day, index) => {
              const hasSpecific = day.hasSpecificSettings;
              const isSelected = selectedDates.includes(day.date);
              const isSourceDate = sourceConfig?.sourceDate === day.date;

              const dayClasses = [
                'calendar-mng-day',
                !day.isCurrentMonth && 'other-month',
                isSelected && multiSelectMode && 'multi-selected',
                hasSpecific && 'has-specific',
                isSourceDate && 'source-date',
                selectedDate === day.date && !multiSelectMode && 'currently-selected'
              ].filter(Boolean).join(' ');


              return (
                <div
                  key={index}
                  className={dayClasses}
                  onClick={() => handleDateClick(day)}
                  title={
                    isSourceDate ? 'Date source pour la copie' :
                      hasSpecific ? 'Disponibilité spécifique configurée' :
                        'Utilise la disponibilité par défaut'
                  }
                >
                  <span className="day-number">{day.dayNumber}</span>

                  {/* Indicateur de disponibilité spécifique */}
                  {hasSpecific && !isSourceDate && (
                    <div className="specific-indicator">●</div>
                  )}

                  {/* Indicateur de date source */}
                  {isSourceDate && (
                    <div className="source-indicator">📋</div>
                  )}

                  {/* Bouton pour définir comme source de copie */}
                  {hasSpecific && multiSelectMode && copyMode !== 'from-template' && !isSourceDate && (
                    <button
                      className="btn-set-copy-source"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAsSourceForCopy(day.date);
                      }}
                      title="Utiliser comme source de copie"
                    >
                      📋
                    </button>
                  )}

                  {/* Bouton de suppression (quand pas en mode copie) */}
                  {hasSpecific && !multiSelectMode && (
                    <button
                      className="btn-delete-specific"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSpecificAvailability(day.date);
                      }}
                      title="Supprimer cette disponibilité spécifique"
                    >
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Légende avec style original */}
        <div class="calendar-mng-legend">
          <div class="legend-item">
            <div class="legend-color default"></div>
            <span>Disponibilité par défaut</span>
          </div>
          <div class="legend-item">
            <div class="legend-color specific"></div>
            <span>Disponibilité spécifique configurée</span>
          </div>
          <div class="legend-item">
            <div class="legend-color multi-selected"></div>
            <span>Dates sélectionnées</span>
          </div>
          <div class="legend-item">
            <div class="legend-color source-date"></div>
            <span>Date source pour copie</span>
          </div>
          <div class="legend-item">
            <div class="legend-color currently-selected"></div>
            <span>Date en cours de configuration</span>
          </div>
        </div>

        {/* Aperçu des dates sélectionnées en mode multiple */}
        {multiSelectMode && selectedDates.length > 0 && (
          <div className="selected-dates-preview">
            <h4>Dates sélectionnées ({selectedDates.length})</h4>
            <div className="selected-dates-grid">
              {selectedDates.slice(0, 10).map(dateStr => (
                <span key={dateStr} className="selected-date-chip">
                  {new Date(dateStr).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'short'
                  })}
                </span>
              ))}
              {selectedDates.length > 10 && (
                <span className="selected-date-chip more">
                  +{selectedDates.length - 10} autres
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="loading">Chargement des disponibilités...</div>;
  }

  return (
    <div className="availability-manager">
      {/* Sélecteur de mode */}
      <div className="availability-mode-selector">
        <h3>Mode de gestion des disponibilités</h3>
        <div className="mode-buttons">
          <button
            className={`btn ${viewMode === 'weekly' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('weekly')}
          >
            📅 Vue hebdomadaire
          </button>
          <button
            className={`btn ${viewMode === 'specific' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('specific')}
          >
            🗓️ Dates spécifiques
          </button>
        </div>
        <p className="mode-description">
          {viewMode === 'weekly'
            ? 'Configurez vos horaires standards qui se répètent chaque semaine.'
            : 'Définissez des horaires particuliers pour des dates précises (congés, horaires exceptionnels...).'
          }
        </p>
      </div>

      {/* Contenu principal */}
      <div className={`availability-content ${editMode ? 'with-form' : ''}`}>
        {viewMode === 'weekly' ? renderWeeklyView() : renderSpecificView()}
        {renderForm()}
      </div>
    </div>
  );
};

export default AvailabilityManager;